"use client";

import { ilikeContainsFilter } from "@/lib/postgrest-filter";
import { supabase } from "@/lib/supabase";
import type { DbProject, DbProjectSharing } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

// ── Cursor pagination ──────────────────────────────────────────────────────────
// Keyset cursor for the (created_at DESC, id DESC) ordering — matches
// idx_projects_active_created_at_id so each page is a single index scan.

export type ProjectsCursor = { createdAt: string; id: string };
export type ProjectsOwnershipFilter = "all" | "created" | "shared";

export function projectCursorFromRow(project: DbProject): ProjectsCursor {
  return { createdAt: project.created_at, id: project.id };
}

export async function fetchProjectsPage(
  cursor: ProjectsCursor | null,
  pageSize: number,
  search?: string,
  ownership: ProjectsOwnershipFilter = "all",
  userId?: string | null
): Promise<DbProject[]> {
  let query = supabase
    .from("projects")
    .select("*")
    .is("archived_at", null)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(pageSize);

  if (userId && ownership === "created") query = query.eq("owner_id", userId);
  if (userId && ownership === "shared") query = query.neq("owner_id", userId);

  const trimmedSearch = search?.trim();
  if (trimmedSearch) {
    query = query.or(
      [
        ilikeContainsFilter("name", trimmedSearch),
        ilikeContainsFilter("description", trimmedSearch),
        ilikeContainsFilter("project_number", trimmedSearch),
      ].join(",")
    );
  }

  if (cursor) {
    query = query.or(
      `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`
    );
  }

  const { data, error } = await query;

  if (error) {
    console.error("[fetchProjectsPage] fetch error:", error);
    return [];
  }

  return (data as DbProject[] | null) ?? [];
}

// ── Pins ─────────────────────────────────────────────────────────────────────
// Per-user pins on the top-level Projects list, capped at PROJECT_PIN_LIMIT
// (enforced by the caller, same convention as project_chat_pins).

export const PROJECT_PIN_LIMIT = 3;

// Dispatched on window after a successful pin/unpin so other mounted views
// (the sidebar's pinned list) refetch without sharing state with the page.
export const PROJECT_PINS_CHANGED_EVENT = "woods:project-pins-changed";

const notifyProjectPinsChanged = () => {
  window.dispatchEvent(new Event(PROJECT_PINS_CHANGED_EVENT));
};

export async function fetchPinnedProjects(userId: string): Promise<DbProject[]> {
  const { data: pins, error: pinsError } = await supabase
    .from("project_pins")
    .select("project_id")
    .eq("user_id", userId)
    .order("pinned_at", { ascending: false });

  if (pinsError) {
    console.error("[fetchPinnedProjects] pins fetch error:", pinsError);
    return [];
  }

  const projectIds = (pins ?? []).map((p) => p.project_id);
  if (projectIds.length === 0) return [];

  const { data: projects, error: projectsError } = await supabase
    .from("projects")
    .select("*")
    .in("id", projectIds)
    .is("archived_at", null);

  if (projectsError) {
    console.error("[fetchPinnedProjects] projects fetch error:", projectsError);
    return [];
  }

  const byId = new Map((projects as DbProject[] | null ?? []).map((p) => [p.id, p]));
  return projectIds.map((id) => byId.get(id)).filter((p): p is DbProject => p != null);
}

export async function pinProject(userId: string, projectId: string): Promise<boolean> {
  const { error } = await supabase
    .from("project_pins")
    .insert({ user_id: userId, project_id: projectId });

  if (error) {
    console.error("[pinProject] insert error:", error);
    return false;
  }
  notifyProjectPinsChanged();
  return true;
}

export async function unpinProject(userId: string, projectId: string): Promise<boolean> {
  const { error } = await supabase
    .from("project_pins")
    .delete()
    .eq("user_id", userId)
    .eq("project_id", projectId);

  if (error) {
    console.error("[unpinProject] delete error:", error);
    return false;
  }
  notifyProjectPinsChanged();
  return true;
}

/** The caller's pinned projects, kept in sync with pin/unpin anywhere in the app. */
export function usePinnedProjects(userId: string | null | undefined): DbProject[] {
  const [pinnedProjects, setPinnedProjects] = useState<DbProject[]>([]);

  useEffect(() => {
    if (!userId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPinnedProjects([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const pins = await fetchPinnedProjects(userId);
      if (!cancelled) setPinnedProjects(pins);
    };
    void load();
    window.addEventListener(PROJECT_PINS_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(PROJECT_PINS_CHANGED_EVENT, load);
    };
  }, [userId]);

  return pinnedProjects;
}

export type UseProjectsReturn = {
  projects: DbProject[];
  loading: boolean;
  refresh: () => void;
  createProject: (params: {
    name: string;
    project_number?: string;
    description?: string;
    instructions?: string;
    sharing?: DbProjectSharing;
  }) => Promise<DbProject | null>;
  updateProject: (
    projectId: string,
    updates: Partial<Pick<DbProject, "name" | "project_number" | "description" | "instructions" | "sharing">>
  ) => Promise<boolean>;
  archiveProject: (projectId: string) => Promise<boolean>;
};

export function useProjects(): UseProjectsReturn {
  const [projects, setProjects] = useState<DbProject[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    setLoading(true);

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setProjects([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .is("archived_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[useProjects] fetch error:", error);
    }

    setProjects((data as DbProject[] | null) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (!cancelled) void fetchProjects();
    });

    return () => {
      cancelled = true;
    };
  }, [fetchProjects]);

  const createProject = useCallback(
    async ({
      name,
      project_number,
      description,
      instructions,
      sharing = "private",
    }: {
      name: string;
      project_number?: string;
      description?: string;
      instructions?: string;
      sharing?: DbProjectSharing;
    }): Promise<DbProject | null> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("projects")
        .insert({
          owner_id: user.id,
          name: name.slice(0, 200),
          project_number: project_number ?? null,
          description: description ?? null,
          instructions: instructions ?? null,
          sharing,
        })
        .select("*")
        .single();

      if (error) {
        console.error("[useProjects] createProject error:", error);
        return null;
      }

      const project = data as DbProject;
      setProjects((current) => [project, ...current]);
      return project;
    },
    []
  );

  const updateProject = useCallback(
    async (
      projectId: string,
      updates: Partial<Pick<DbProject, "name" | "project_number" | "description" | "instructions" | "sharing">>
    ): Promise<boolean> => {
      const { error } = await supabase
        .from("projects")
        .update(updates)
        .eq("id", projectId);

      if (error) {
        console.error("[useProjects] updateProject error:", error);
        return false;
      }

      setProjects((current) =>
        current.map((p) => (p.id === projectId ? { ...p, ...updates } : p))
      );
      return true;
    },
    []
  );

  const archiveProject = useCallback(async (projectId: string): Promise<boolean> => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from("projects")
      .update({ archived_at: new Date().toISOString(), archived_by: user.id })
      .eq("id", projectId);

    if (error) {
      console.error("[useProjects] archiveProject error:", error);
      return false;
    }

    setProjects((current) => current.filter((p) => p.id !== projectId));
    return true;
  }, []);

  return { projects, loading, refresh: fetchProjects, createProject, updateProject, archiveProject };
}
